import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BookOpen, Target, FileText, Briefcase, ShieldCheck, Trash2, RefreshCw } from 'lucide-react';

const date = (value) => value ? new Date(value).toLocaleDateString('en-IN') : '';
const section = 'rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)] p-5';
const muted = 'text-sm text-[var(--text-muted)]';
const button = 'rounded-lg border border-[var(--border-default)] px-3 py-2 text-sm font-semibold';

export default function CareerCompanionDashboard() {
  const [data, setData] = useState(null);
  const [choices, setChoices] = useState({ memory: false, recruiterSharing: false, serviceRecommendations: false });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [notice, setNotice] = useState('');

  const load = async () => {
    try {
      const { data: result } = await axios.get('/api/career-companion/me');
      setData(result);
      setChoices({
        memory: Boolean(result.consent?.memory),
        recruiterSharing: Boolean(result.consent?.recruiterSharing),
        serviceRecommendations: Boolean(result.consent?.serviceRecommendations),
      });
      setProblem('');
    } catch (e) { setProblem(e.response?.data?.error || 'Unable to load your career dashboard'); }
  };
  useEffect(() => { load(); }, []);

  const saveChoices = async () => {
    setBusy(true); setNotice('');
    try {
      await axios.put('/api/career-companion/consent', choices);
      await load();
      setNotice('Your preferences have been saved.');
    } catch (e) { setProblem(e.response?.data?.error || 'Could not update preferences'); }
    finally { setBusy(false); }
  };
  const removeInsight = async id => {
    if (!window.confirm('Delete this career insight?')) return;
    try { await axios.delete('/api/career-companion/insights/' + id); await load(); }
    catch (e) { setProblem(e.response?.data?.error || 'Unable to delete insight'); }
  };
  const eraseJourney = async () => {
    if (window.prompt('Type DELETE MY CAREER JOURNEY to erase your saved career workspace') !== 'DELETE MY CAREER JOURNEY') return;
    setBusy(true);
    try {
      await axios.delete('/api/career-companion/me', { data: { confirmation: 'DELETE MY CAREER JOURNEY' } });
      await load(); setNotice('Career companion workspace deleted. Your standard HireVeda profile is unchanged.');
    } catch (e) { setProblem(e.response?.data?.error || 'Unable to erase workspace'); }
    finally { setBusy(false); }
  };

  if (!data) return <main className="mx-auto max-w-5xl p-6" aria-live="polite">{problem || 'Loading career dashboard...'}</main>;
  const profile = data.candidate || {};
  const onboard = data.onboarding || {};
  const interests = data.insights || [];
  const sessions = (data.sessions || []).slice().reverse();
  const resumes = (data.tailoredResumes || []).slice().reverse();
  const apps = (data.applications || []).slice().reverse();

  return (
    <main className="mx-auto max-w-5xl space-y-6 px-4 py-8 text-[var(--text-primary)]">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className={muted}>Your private career workspace</p>
          <h1 className="text-2xl font-bold">Career Companion</h1>
          <p className={muted}>Your goals, strengths, contributions, resumes and next steps in one place.</p>
        </div>
        <button className={button} onClick={load}><RefreshCw size={16} className="mr-2 inline" />Refresh</button>
      </header>
      {problem && <p role="alert" className="rounded-lg border border-red-400 p-3 text-sm">{problem}</p>}
      {notice && <p role="status" className="rounded-lg border p-3 text-sm">{notice}</p>}
      {data.legalAcceptanceRequired && (
        <p role="alert" className="rounded-lg border p-4 text-sm">
          Before saving career notes, please <Link className="font-semibold underline" to="/legal/accept">review and accept HireVeda's legal terms</Link>.
        </p>
      )}

      <section className={section}>
        <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold"><ShieldCheck size={20} /> Your data controls</h2>
        <p className={muted}>Your assistant can save structured career insights only with your permission. Recruiter sharing and HireVeda service recommendations are optional. Turning off memory stops new saves; use Delete workspace to erase existing career notes.</p>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {[
            ['memory', 'Remember my career journey', 'Save goals, evidence-backed insights, approved resumes and summaries.'],
            ['recruiterSharing', 'Allow recruiter sharing', 'Opt in to future explicit sharing. This alone does not send your data to recruiters.'],
            ['serviceRecommendations', 'Show relevant services', 'Allow clear, relevant suggestions about paid HireVeda/ExecEdge options.'],
          ].map(([key, label, description]) => (
            <label key={key} className="flex cursor-pointer gap-3 rounded-lg border border-[var(--border-default)] p-3">
              <input type="checkbox" className="mt-1" checked={choices[key]}
                disabled={key !== 'memory' && !choices.memory}
                onChange={e => setChoices(prev => ({
                  ...prev, [key]: e.target.checked,
                  ...((key === 'memory' && !e.target.checked) ? { recruiterSharing: false, serviceRecommendations: false } : {}),
                }))} />
              <span><strong className="block text-sm">{label}</strong><span className={muted}>{description}</span></span>
            </label>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <button className={button} onClick={saveChoices} disabled={busy || data.legalAcceptanceRequired}>Save preferences</button>
          <button className={button + ' text-red-600'} onClick={eraseJourney} disabled={busy}><Trash2 size={16} className="mr-2 inline" />Delete workspace</button>
        </div>
      </section>

      {!data.consent?.memory ? (
        <section className={section}>
          <h2 className="text-lg font-semibold">Start your career journey</h2>
          <p className={muted}>Turn on career memory above, then connect HireVeda Career Companion to ChatGPT or Claude to explore your goals and build your career portfolio. You can use the assistant without storing notes until you opt in.</p>
        </section>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            <section className={section}>
              <h2 className="mb-3 flex items-center gap-2 font-semibold"><Briefcase size={18} /> Current position</h2>
              <p className="font-semibold">{profile.currentRole || 'Current role not recorded'}</p>
              <p className={muted}>{[profile.currentCompany, profile.currentLocation].filter(Boolean).join(' · ')}</p>
              <p className="mt-2 text-sm">{profile.headline || 'Build your positioning through the assistant.'}</p>
            </section>
            <section className={section}>
              <h2 className="mb-3 flex items-center gap-2 font-semibold"><Target size={18} /> Next career move</h2>
              <p className="font-semibold">{onboard.primaryGoal || 'Your career goal is not yet defined'}</p>
              <p className={muted}>Target roles: {(onboard.targetRoles || []).join(', ') || 'To be explored'}</p>
              <p className={muted}>Locations: {(onboard.targetLocations || []).join(', ') || 'Flexible'}</p>
            </section>
          </div>
          <section className={section}>
            <h2 className="mb-2 text-lg font-semibold">Career positioning</h2>
            <p className="font-semibold">{data.positioning?.headline || 'Positioning not drafted yet'}</p>
            <p className="mt-2 text-sm">{data.positioning?.valueProposition || 'Ask your career companion to map demonstrated strengths, trade-offs and your next positioning.'}</p>
            {(data.positioning?.developmentPriorities || []).length > 0 && <p className="mt-2 text-sm">Growth priorities: {data.positioning.developmentPriorities.join(', ')}</p>}
          </section>
          <section className={section}>
            <h2 className="mb-3 text-lg font-semibold">Your career insights ({interests.length})</h2>
            {!interests.length && <p className={muted}>Meaningful insights will appear after your conversations and approval.</p>}
            <div className="space-y-3">{interests.slice().reverse().map(item => (
              <div key={item._id} className="flex items-start justify-between gap-3 border-b border-[var(--border-default)] pb-3">
                <div>
                  <p className="text-sm font-semibold">{item.topic} <span className={muted}>· {item.confirmed ? 'Candidate-confirmed' : 'Unconfirmed hypothesis'}</span></p>
                  <p className="text-sm">{item.statement}</p>
                  {item.example && <p className={muted}>Evidence: {item.example}</p>}
                </div>
                <button className={button} onClick={() => removeInsight(item._id)}>Remove</button>
              </div>
            ))}</div>
          </section>
          <section className={section}>
            <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold"><FileText size={20} /> Resume library</h2>
            <p className="text-sm font-semibold">Master resume: {data.masterResume?.title || 'Not drafted'}</p>
            {data.masterResume?.content && <details className="mt-2"><summary className="cursor-pointer text-sm">View master resume</summary><pre className="mt-2 whitespace-pre-wrap rounded-lg border p-4 text-sm">{data.masterResume.content}</pre></details>}
            <h3 className="mt-4 text-sm font-semibold">Tailored resumes ({resumes.length})</h3>
            <ul className="mt-2 space-y-2">{resumes.map(r => (
              <li key={r._id}><details><summary className="cursor-pointer text-sm">{r.title} · {r.targetRole || 'General'} · {date(r.createdAt)}</summary><pre className="whitespace-pre-wrap rounded-lg border p-4 text-sm">{r.content}</pre></details></li>
            ))}</ul>
          </section>
          <section className={section}>
            <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold"><Briefcase size={20} /> Job tracker ({apps.length})</h2>
            <p className={muted}>Applications from public jobs and candidate-approved Gmail/Outlook connector findings. Email-derived statuses are not independently verified with employers.</p>
            {!apps.length ? <p className={muted}>Saved jobs and approved applications will appear here.</p> : <ul className="mt-3 space-y-2">{apps.map(a => <li key={a._id} className="text-sm"><span className="font-semibold">{a.status}</span> · {a.job?.url ? <a className="underline" href={a.job.url} target="_blank" rel="noopener noreferrer">{a.job.title || "Job opportunity"}</a> : (a.roleTitle || a.job?.title || "Job opportunity")} {(a.companyName || a.job?.company) && `at ${a.companyName || a.job?.company}`} · {a.source === 'gmail' || a.source === 'outlook' ? `Imported from ${a.source}` : 'HireVeda job'} · {date(a.messageDate || a.updatedAt)} {a.note && " · " + a.note}</li>)}</ul>}
          </section>
          <section className={section}>
            <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold"><BookOpen size={20} /> Career conversation summaries ({sessions.length})</h2>
            <p className={muted}>These are summaries you explicitly approved, not automatic copies of your ChatGPT or Claude chats.</p>
            <div className="mt-3 space-y-4">{sessions.map(item => (
              <article className="border-b border-[var(--border-default)] pb-3" key={item._id}>
                <h3 className="text-sm font-semibold">{item.focus} · {date(item.createdAt)}</h3>
                <p className="text-sm">{item.summary}</p>
                {!!item.nextActions?.length && <p className={muted}>Next: {item.nextActions.join('; ')}</p>}
              </article>
            ))}</div>
          </section>
        </>
      )}
      <p className={muted}>Want to discover opportunities? <Link to="/hv/jobs" className="underline">Browse jobs on HireVeda</Link>.</p>
    </main>
  );
}
